import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import TooltipWrapper from 'modules/shift_management/components/ShiftManagement/TooltipWrapper'
import { type Duration, hoursToDuration } from 'modules/shift_management/lib/hoursToDuration'
import type { View } from 'modules/shift_management/lib/types'
import { VIEW_TYPES } from 'modules/shift_management/store/internal/models/View'

export interface InformationTooltipProps {
  view: View
  hours: number
  absencesHours?: number | null
  unit: string | null
  days: string | null
  plannedHours: Duration
  showPlannedHours: boolean
  showContractHours: boolean
  children: React.ReactNode
}

type ViewUnit = 'daily' | 'weekly' | 'monthly' | 'four-weeks'

const viewTypes: Record<string, ViewUnit> = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  fourWeeks: 'four-weeks',
}

const getTooltipTitle = (view: View): string => {
  switch (view.viewType) {
    case VIEW_TYPES.monthly:
      return i18n.t('shift_management.hours_information.tooltip.monthly_title')

    case VIEW_TYPES.fourWeeks:
      return i18n.t('shift_management.hours_information.tooltip.four_weeks_title')

    default:
      return i18n.t('shift_management.hours_information.tooltip.weekly_title')
  }
}

const getPlannedHoursCopyTooltip = (plannedHours: Duration): string => {
  const { hours, minutes } = plannedHours
  return i18n.t('shift_management.hours_information.tooltip.planned_hours.info', {
    hours,
    minutes,
  })
}

const getDaysFrequencyCopy = (days: string | null): string => {
  const daysFrequency = days?.split(',').length
  return daysFrequency
    ? i18n.tp('shift_management.hours_information.tooltip.contract_hours.frequency', {
        count: daysFrequency,
      })
    : i18n.t('shift_management.hours_information.tooltip.contract_hours.no_weekdays')
}

const InformationTooltip = ({
  view,
  hours,
  absencesHours,
  unit,
  days,
  plannedHours,
  showPlannedHours,
  showContractHours,
  children,
}: InformationTooltipProps) => {
  const showTooltipWithAbsences = absencesHours != null && absencesHours >= 0

  const getAbsencesContractHoursCopyTooltip = (viewType: string): string => {
    const title = i18n.t('shift_management.hours_information.tooltip.contract_hours.absences.title')
    const params = {
      hours,
      leaves_hours: absencesHours,
    }
    switch (viewType) {
      case viewTypes.daily:
        return `${title} ${i18n.t(
          'shift_management.hours_information.tooltip.contract_hours.absences.frequency_description.daily',
          params
        )}`

      case viewTypes.weekly:
        return `${title} ${i18n.t(
          'shift_management.hours_information.tooltip.contract_hours.absences.frequency_description.weekly',
          params
        )}`

      case viewTypes.monthly:
        return `${title} ${i18n.t(
          'shift_management.hours_information.tooltip.contract_hours.absences.frequency_description.monthly',
          params
        )}`

      case viewTypes.fourWeeks:
        return `${title} ${i18n.t(
          'shift_management.hours_information.tooltip.contract_hours.absences.frequency_description.four_weeks',
          params
        )}`

      default:
        return ''
    }
  }

  const fullViewType = (viewType: string): string => {
    switch (viewType) {
      case viewTypes.daily:
        return i18n.t('shift_management.hours_information.contract_hours.per_day')

      case viewTypes.weekly:
        return i18n.t('shift_management.hours_information.contract_hours.per_week')

      case viewTypes.monthly:
        return i18n.t('shift_management.hours_information.contract_hours.per_month')

      case viewTypes.fourWeeks:
        return i18n.t('shift_management.hours_information.contract_hours.per_four_weeks')

      default:
        return ''
    }
  }

  const getContractHoursCopyTooltip = (viewType: string): string => {
    if (!hours || !unit)
      return i18n.t('shift_management.hours_information.tooltip.contract_hours.no_hours')

    const duration = hoursToDuration(hours)

    const copy = i18n.t('shift_management.hours_information.tooltip.contract_hours.new_assigned', {
      hours: duration.hours,
      minutes: duration.minutes,
    })

    return `${copy} ${fullViewType(viewType)}`
  }

  return (
    <TooltipWrapper.ItemList
      label={
        <Box gap="s8" alignItems="center">
          <Text.Header3>{getTooltipTitle(view)}</Text.Header3>
          {showPlannedHours && (
            <Box alignItems="center">
              <Text.Normal>
                {i18n.t('shift_management.hours_information.tooltip.planned_hours.label')}
              </Text.Normal>
              <Text.Normal size="200">{getPlannedHoursCopyTooltip(plannedHours)}</Text.Normal>
            </Box>
          )}
          {showContractHours && (
            <Box alignItems="center">
              <Text.Normal>
                {i18n.t('shift_management.hours_information.tooltip.contract_hours.label')}
              </Text.Normal>
              {showTooltipWithAbsences ? (
                <Text.Normal size="200">
                  {getAbsencesContractHoursCopyTooltip(view.viewType)}
                </Text.Normal>
              ) : (
                <>
                  <Text.Normal size="200">{getContractHoursCopyTooltip(view.viewType)}</Text.Normal>
                  <Text.Normal size="200">{getDaysFrequencyCopy(days)}</Text.Normal>
                </>
              )}
            </Box>
          )}
        </Box>
      }
    >
      {children}
    </TooltipWrapper.ItemList>
  )
}

export default observer(InformationTooltip)
